import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, KeyRound, ArrowRight, Calendar, Mail, X, ShieldCheck, Activity } from "lucide-react";
import {
  useDemoAccess,
  canPerform,
  trackEvent,
} from "@/lib/demoAccess";

/**
 * DemoAccessGate — premium operational gate for restricted demo surfaces.
 *
 * Wraps any restricted route. If the current session's access level
 * does NOT include the requested `action`, the gate renders an in-page
 * overlay (NOT a hard redirect) explaining what the surface would show
 * and offering two premium CTAs:
 *   • Request Extended Access  → emits `cta_clicked` + thank-you state
 *   • Schedule Operational Walkthrough → same pattern
 *
 * The visual is intentionally restrained — dark, mono, mission-critical.
 * Reads like a NOC permission gate, NOT a SaaS "upgrade" modal.
 */
export function DemoAccessGate({ action, children, label, labelEs }) {
  const { access } = useDemoAccess();
  const lang = (typeof window !== "undefined"
    ? window.localStorage.getItem("forgedops.lang") || "en"
    : "en");

  if (canPerform(access, action)) {
    return children;
  }

  return <AccessRequiredOverlay action={action} label={label} labelEs={labelEs} lang={lang} />;
}

/* ─────────────────────────── Access Required overlay ─────────────────────────── */
function AccessRequiredOverlay({ action, label, labelEs, lang }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { access } = useDemoAccess();
  const [submitted, setSubmitted] = useState(null); // null | "extended" | "walkthrough"
  const [showTokenEntry, setShowTokenEntry] = useState(false);

  const isExpired = access?.access_level === "public_preview" &&
    (typeof window !== "undefined" &&
      window.localStorage.getItem("forgeops.demo.lasterror") === "expired");

  const t = (en, es) => (lang === "es" ? es : en);

  const surfaceLabel =
    (lang === "es" && labelEs) ? labelEs :
    (label || t("Restricted operational surface", "Superficie operativa restringida"));

  const submitCta = useCallback(async (kind) => {
    await trackEvent("cta_clicked", {
      cta: kind,
      path: location.pathname,
      action_required: action,
      current_access_level: access?.access_level || "public_preview",
    });
    setSubmitted(kind);
  }, [action, access, location.pathname]);

  if (showTokenEntry) {
    return (
      <TokenEntryModal
        onClose={() => setShowTokenEntry(false)}
        onSuccess={() => {
          // After successful validation the provider rehydrates from
          // localStorage on next render; the simplest reliable way to
          // pick that up is a full reload.
          window.location.reload();
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(10,10,10,0.97) 0%, rgba(0,0,0,0.99) 100%)",
        backdropFilter: "blur(8px)",
      }}
      data-testid="demo-access-required-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-access-required-title"
    >
      <div
        className="w-full max-w-[640px] relative"
        style={{
          background: "#0B0B0B",
          border: "1px solid #1F1F1F",
          boxShadow:
            "0 0 0 1px rgba(255,106,0,0.18), 0 32px 80px rgba(0,0,0,0.75)",
        }}
      >
        {/* Caution stripe top */}
        <div
          aria-hidden
          style={{
            height: 4,
            background:
              "repeating-linear-gradient(45deg, #FF6A00 0 12px, #0A0A0A 12px 24px)",
          }}
        />

        <div className="px-6 sm:px-8 py-7">
          {/* Eyebrow */}
          <div
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] mb-5"
            style={{ color: "#FF6A00" }}
          >
            <Lock className="w-3 h-3" />
            <span>
              {isExpired
                ? t("Operational Sandbox · Access Revoked",
                     "Caja Operativa · Acceso Revocado")
                : t("Operational Sandbox · Access Required",
                     "Caja Operativa · Acceso Requerido")}
            </span>
          </div>

          {/* Headline */}
          <h2
            id="demo-access-required-title"
            className="text-2xl sm:text-[28px] leading-tight font-bold mb-3"
            style={{ color: "#FFFFFF", letterSpacing: "-0.01em" }}
          >
            {isExpired
              ? t("This sandbox session has expired.",
                  "Esta sesión de caja operativa ha expirado.")
              : t("This surface is gated behind an operational sandbox key.",
                  "Esta superficie está protegida por una clave de caja operativa.")}
          </h2>

          <p
            className="text-sm leading-relaxed mb-6"
            style={{ color: "#8A8A8A" }}
          >
            {t(
              "Public preview shows the landing experience and the live operational auto-tour. Deep workflows — role hubs, form submissions, executive dashboards, and white-label configurations — run inside the qualified sandbox.",
              "La vista previa pública muestra la experiencia de inicio y el tour operativo en vivo. Los flujos profundos — hubs de rol, envíos, paneles ejecutivos y configuraciones de marca propia — viven dentro de la caja operativa calificada."
            )}
          </p>

          {/* What this surface would show */}
          <div
            className="mb-6 px-4 py-3"
            style={{
              background: "#0A0A0A",
              border: "1px solid #1A1A1A",
              borderLeft: "2px solid #FF6A00",
            }}
            data-testid="demo-gate-surface-summary"
          >
            <div
              className="font-mono text-[9px] uppercase tracking-[0.3em] mb-1.5"
              style={{ color: "#666" }}
            >
              {t("Requested surface", "Superficie solicitada")}
            </div>
            <div className="text-sm font-medium" style={{ color: "#FFFFFF" }}>
              {surfaceLabel}
            </div>
            <div
              className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1.5"
              style={{ color: "#FF6A00" }}
            >
              {t("Required clearance", "Permiso requerido")} ·{" "}
              {accessLevelForAction(action)}
            </div>
          </div>

          {/* CTAs / Thank-you state */}
          {submitted ? (
            <ThankYouState lang={lang} kind={submitted} onBack={() => setSubmitted(null)} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <button
                onClick={() => submitCta("request_extended_access")}
                className="group flex items-center justify-between px-4 py-3 transition-colors"
                style={{
                  background: "#FF6A00",
                  color: "#0A0A0A",
                  border: "1px solid #FF6A00",
                }}
                data-testid="demo-gate-cta-extended"
              >
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold">
                    {t("Request extended access", "Solicitar acceso extendido")}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>

              <button
                onClick={() => submitCta("schedule_walkthrough")}
                className="group flex items-center justify-between px-4 py-3 transition-colors"
                style={{
                  background: "#101010",
                  color: "#FFFFFF",
                  border: "1px solid #2A2A2A",
                }}
                data-testid="demo-gate-cta-walkthrough"
              >
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" style={{ color: "#FF6A00" }} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold">
                    {t("Schedule walkthrough", "Agendar recorrido")}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          )}

          {/* Token entry + back to demo */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t" style={{ borderColor: "#1A1A1A" }}>
            <button
              onClick={() => setShowTokenEntry(true)}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: "#FF6A00" }}
              data-testid="demo-gate-enter-token"
            >
              <KeyRound className="w-3 h-3" />
              {t("Enter access key", "Ingresar clave de acceso")}
            </button>

            <button
              onClick={() => navigate("/demo")}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: "#888" }}
              data-testid="demo-gate-back"
            >
              {t("← Back to live demo", "← Volver al demo en vivo")}
            </button>
          </div>
        </div>

        {/* Bottom legal hairline */}
        <div
          className="px-6 sm:px-8 py-2.5 font-mono text-[9px] uppercase tracking-[0.28em] text-center"
          style={{
            background: "#080808",
            color: "#555",
            borderTop: "1px solid #161616",
          }}
        >
          {t("ForgedOps · Operational Sandbox · Controlled Access",
             "ForgedOps · Caja Operativa · Acceso Controlado")}
        </div>
      </div>
    </div>
  );
}

function accessLevelForAction(action) {
  // Action → minimum access level required (display only).
  const QUALIFIED_ACTIONS = new Set([
    "white_label_preview", "client_branding_examples",
    "integration_examples", "deployment_architecture",
    "executive_walkthrough",
  ]);
  if (QUALIFIED_ACTIONS.has(action)) return "QUALIFIED";
  return "SANDBOX";
}

function ThankYouState({ lang, kind, onBack }) {
  const t = (en, es) => (lang === "es" ? es : en);
  const headline = kind === "request_extended_access"
    ? t("Access request logged. The ForgedOps team will reach out shortly.",
        "Solicitud registrada. El equipo de ForgedOps se contactará pronto.")
    : t("Walkthrough request logged. We'll be in touch to schedule.",
        "Recorrido solicitado. Nos pondremos en contacto para agendar.");

  return (
    <div
      className="mb-5 px-4 py-4"
      style={{
        background: "#0A0A0A",
        border: "1px solid #1A1A1A",
        borderLeft: "2px solid #2E7D32",
      }}
      data-testid="demo-gate-thankyou"
    >
      <div
        className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] mb-1.5"
        style={{ color: "#2E7D32" }}
      >
        <Activity className="w-3 h-3" />
        {t("Operational handoff queued", "Entrega operativa en cola")}
      </div>
      <div className="text-sm" style={{ color: "#FFFFFF" }}>
        {headline}
      </div>
      <div className="text-xs mt-1" style={{ color: "#666" }}>
        {t("Reference: a session telemetry event has been routed to the sales operations queue.",
           "Referencia: un evento de telemetría ha sido enrutado a la cola de operaciones de ventas.")}
      </div>
      <button
        type="button"
        onClick={onBack}
        className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em]"
        style={{ color: "#888" }}
        data-testid="demo-gate-thankyou-back"
      >
        ← {t("Back", "Atrás")}
      </button>
    </div>
  );
}

/* ─────────────────────────── Token Entry Modal ─────────────────────────── */
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function TokenEntryModal({ onClose, onSuccess }) {
  const lang = (typeof window !== "undefined"
    ? window.localStorage.getItem("forgedops.lang") || "en"
    : "en");
  const t = (en, es) => (lang === "es" ? es : en);

  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!token.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/demo/validate-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token.trim() }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const reason = body?.detail?.reason || "invalid";
        const message = body?.detail?.message || t("Token not recognized.", "Clave no reconocida.");
        try { window.localStorage.setItem("forgeops.demo.lasterror", reason); } catch {}
        setErr({ reason, message });
        setBusy(false);
        return;
      }
      const body = await r.json();
      // Hydrate the access store before reloading so the new mode is
      // visible immediately.
      const modesRes = await fetch(`${API_BASE}/api/demo/access-modes`).catch(() => null);
      const modes = modesRes ? await modesRes.json().catch(() => null) : null;
      const merged = {
        ...body,
        valid: true,
        allow: modes?.[body.access_level]?.allow || [],
      };
      try {
        window.localStorage.setItem("forgeops.demo.access", JSON.stringify(merged));
        window.localStorage.removeItem("forgeops.demo.lasterror");
      } catch {}
      onSuccess?.(merged);
    } catch (e2) {
      setErr({ reason: "network", message: t("Network error. Try again.", "Error de red. Inténtalo de nuevo.") });
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6"
      style={{
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(8px)",
      }}
      role="dialog"
      aria-modal="true"
      data-testid="demo-token-entry-modal"
    >
      <div
        className="w-full max-w-[480px] relative"
        style={{
          background: "#0B0B0B",
          border: "1px solid #1F1F1F",
          boxShadow: "0 0 0 1px rgba(255,106,0,0.18), 0 32px 80px rgba(0,0,0,0.75)",
        }}
      >
        <div
          aria-hidden
          style={{
            height: 4,
            background:
              "repeating-linear-gradient(45deg, #FF6A00 0 12px, #0A0A0A 12px 24px)",
          }}
        />
        <div className="px-6 py-6">
          <div className="flex items-start justify-between mb-5">
            <div
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em]"
              style={{ color: "#FF6A00" }}
            >
              <KeyRound className="w-3 h-3" />
              {t("Operational Sandbox · Access Key", "Caja Operativa · Clave")}
            </div>
            <button
              onClick={onClose}
              className="text-[#666] hover:text-white"
              data-testid="demo-token-entry-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <h3 className="text-xl font-bold mb-2" style={{ color: "#FFFFFF" }}>
            {t("Enter your access key.", "Ingresa tu clave de acceso.")}
          </h3>
          <p className="text-sm mb-5" style={{ color: "#8A8A8A" }}>
            {t(
              "Paste the access key from your ForgedOps demo invitation. Keys are single-prospect, time-limited, and never shared.",
              "Pega la clave de tu invitación de demo ForgedOps. Las claves son de un solo prospecto, con tiempo limitado y no se comparten."
            )}
          </p>

          <form onSubmit={submit}>
            <input
              type="text"
              autoFocus
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="SBX-XXXX-XXX / QLF-XXXX-XXX"
              className="w-full px-3 py-3 font-mono text-sm tracking-[0.08em] mb-4"
              style={{
                background: "#0A0A0A",
                color: "#FFFFFF",
                border: `1px solid ${err ? "#D32F2F" : "#2A2A2A"}`,
                outline: "none",
              }}
              data-testid="demo-token-entry-input"
            />
            {err && (
              <div
                className="px-3 py-2 mb-4 font-mono text-[11px]"
                style={{
                  background: "#1A0808",
                  color: "#FF7070",
                  border: "1px solid #3A1A1A",
                }}
                data-testid="demo-token-entry-error"
              >
                {err.message}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !token.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 transition-colors disabled:opacity-50"
              style={{
                background: "#FF6A00",
                color: "#0A0A0A",
                border: "1px solid #FF6A00",
              }}
              data-testid="demo-token-entry-submit"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold">
                {busy
                  ? t("Validating…", "Validando…")
                  : t("Unlock sandbox", "Desbloquear caja")}
              </span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          <div
            className="mt-4 pt-3 font-mono text-[9px] uppercase tracking-[0.28em]"
            style={{ color: "#555", borderTop: "1px solid #161616" }}
          >
            {t("No key? Use the request access CTA on any gated surface.",
               "¿Sin clave? Usa el CTA de solicitar acceso en cualquier superficie protegida.")}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemoAccessGate;
